/**
 * ROAR-4943 (ROAR-4899 §4 decision 7) — the single source of truth for AC2's usage colour bands.
 *
 * AC2 fixes the thresholds at **50% (yellow)** and **80% (red)**. Every consumer — the per-lot bar
 * in `CreditLotSummary` and the pane-level banner in `CreditsUsage/index.js` — resolves through
 * `resolveUsageBand` so a threshold can never be restated at a call site and drift.
 *
 * The bands are applied **per lot**: each block in the `lots` array colours its own bar from its
 * own `pct`, so a nearly-exhausted grant reads red even when the subscription lot is healthy. The
 * banner, by contrast, renders **once per pane** for the worst band across lots (§4 decision 7) —
 * several stacked warnings do not fit the modal.
 *
 * `resolveUsageBand` takes **only `pct`**. `pct` is backend-computed (the Story's Scope names the
 * "usage-color-threshold calc" as server-side), so the FE never divides, and a 0-allotment lot
 * arrives as `pct: 0`; its presentation guard lives in `CreditLotSummary`, not here.
 */

/**
 * Ordered high threshold first — `resolveUsageBand` returns the FIRST entry whose `minPct` the
 * value clears, so this order is load-bearing, not cosmetic.
 *
 * `banner` is null for the default band: below 50% there is nothing to warn about, and that null
 * is what the pane checks rather than re-comparing the percentage.
 */
export const USAGE_BANDS = [
  {
    minPct: 80,
    tone: 'critical',
    barClassName: 'bg-functional-red',
    /**
     * The banner is a TINTED CARD, not coloured text: a pale fill, a solid band-coloured border,
     * a band-coloured icon and ordinary heading-coloured copy. The sentence carries the same
     * weight in both bands — only the chrome escalates — so the red banner does not read as an
     * error message when it is a usage warning.
     */
    banner: {
      className: 'border-functional-red bg-[#FFF4F4] text-headings',
      iconClassName: 'text-functional-red',
    },
  },
  {
    minPct: 50,
    tone: 'warning',
    // AC2's "yellow". `--functional-yellow` (#FFE765) is legible as a filled bar but not against
    // the banner's pale fill, so the ICON drops to a darker amber (#E5B700) while the border keeps
    // the token. Using the token for the icon too would render it all but invisible.
    barClassName: 'bg-functional-yellow',
    banner: {
      className: 'border-functional-yellow bg-[#FFFDEB] text-headings',
      iconClassName: 'text-[#E5B700]',
    },
  },
  {
    minPct: 0,
    tone: 'default',
    barClassName: 'bg-primary-500',
    banner: null,
  },
];

/**
 * Normalizes a `pct` into `[0, 100]`.
 *
 * Anything that is not a finite number becomes 0 — a missing field, `null`, or a malformed value
 * must render an empty bar, never NaN width or a crash. A numeric STRING is accepted on purpose:
 * Postgres `NUMERIC` columns serialize as strings through Sequelize, so `"32.5"` is a legitimate
 * wire value and dropping it to 0 would silently blank a real bar.
 *
 * @param {number|string} pct Raw percentage from the credits summary.
 * @returns {number} A finite number in `[0, 100]`.
 */
export const clampPct = (pct) => {
  const value = Number(pct);
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

/**
 * A lot's usage percentage for presentation.
 *
 * The `allotment === 0` guard of §4 decision 7. The contract says a 0-allotment lot is returned
 * with `pct: 0`, but "0% of nothing used" is the only percentage that means anything for such a
 * lot, so it is enforced rather than trusted: a server-side divide-by-zero artefact
 * (`allotment: 0, pct: 100`) would otherwise paint a full red bar AND fire a pane banner warning
 * about a lot that grants no credits at all.
 *
 * Kept next to the bands but separate from `resolveUsageBand`, which by decision 7 takes only
 * `pct`. Both the bar and the banner read a lot's percentage through here, so the two cannot
 * disagree about what a lot is at.
 *
 * @param {object} lot A credit lot.
 * @returns {number|string} The percentage to present, still unclamped.
 */
export const effectiveLotPct = (lot) => (lot?.allotment === 0 ? 0 : lot?.pct);

/**
 * Whether a lot has used its whole allotment.
 *
 * Read through `effectiveLotPct` so a zero-allotment lot (the divide-by-zero artefact above) is
 * never reported as exhausted. Exhaustion is not a band of its own: the bar and banner chrome stay
 * critical-red, only the banner COPY changes, so `USAGE_BANDS` and every `tone` comparison are
 * untouched.
 *
 * @param {object} lot A credit lot.
 * @returns {boolean}
 */
export const isLotExhausted = (lot) => clampPct(effectiveLotPct(lot)) >= 100;

/**
 * The band a percentage falls into.
 *
 * Total by construction: the input is clamped first and the table ends at `minPct: 0`, so this
 * never returns `undefined` and callers can read `.tone`/`.banner` without a guard. That matters
 * because the pane computes the worst band across lots — but the pane still short-circuits on an
 * EMPTY `lots` array rather than calling `Math.max()` on it, since it also has to name the lot the
 * banner came from.
 *
 * @param {number|string} pct Raw percentage from the credits summary.
 * @returns {object} The matching entry of USAGE_BANDS.
 */
export const resolveUsageBand = (pct) => {
  const value = clampPct(pct);
  return USAGE_BANDS.find((band) => value >= band.minPct);
};

/**
 * The banner sentence for a band, phrased from the lot it came from.
 *
 * Naming the lot is what stops the banner mislabelling a `beta` or `manual` grant as "this
 * cycle" — only a `subscription` lot is the cycle. The threshold is interpolated from the band's
 * own `minPct`, so the copy and the trigger cannot drift apart. (Figma's ≥50% frame reads "above
 * 60%" while triggering at 58%; that copy is stale and AC2 is authoritative.)
 *
 * @param {object} band A USAGE_BANDS entry, as returned by `resolveUsageBand`.
 * @param {object} lot The lot the band was resolved from (`source`, `label`).
 * @param {string} resetDate Pre-formatted reset date, e.g. "Aug 1".
 * @returns {string|null} The sentence, or null when this band shows no banner.
 */
export const buildUsageBannerMessage = (band, lot, resetDate) => {
  if (!band?.banner) return null;
  // At 100% "used over 80%" understates it — the team cannot spend any more until the reset.
  if (isLotExhausted(lot)) {
    if (lot?.source === 'subscription') {
      return `You've reached your credit limit for this month. Your balance will reset on ${resetDate}.`;
    }
    return `You've reached your ${lot?.label} credit limit. Your balance will reset on ${resetDate}.`;
  }
  if (lot?.source === 'subscription') {
    return `Your team has used over ${band.minPct}% of this cycle's credits, resets ${resetDate}.`;
  }
  return `Your team has used over ${band.minPct}% of your ${lot?.label} credits, resets ${resetDate}.`;
};
